import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppAction, AppContext } from "helpers/context";
import TryAgain from "components/tryAgain/tryAgain";
import { Box, LinearProgress } from "@mui/material";
import { LegendItem } from "helpers/models";
import PolygonStatisticsCard from "./polygon-statistics-card";
import { PolygonLayerStatistic } from "./types";
import styles from "./polygon-result.module.scss";
import { MapContext } from "helpers/map";
import {
  buildPolygonLayerHexagons,
  groupPolygonAnalysisResultsByLayer,
  normalizeResolution,
  PolygonAnalysisResultItem
} from "./polygon-hexagon-utils";

function PolygonResults() {
  const [t] = useTranslation();
  const [context, dispatch] = useContext(AppContext);
  const { map, AMap } = useContext(MapContext);
  const hexagonOverlaysRef = useRef<any[]>([]);

  const polygonLegends = (context.polygonAnalysisLegends || []) as LegendItem[];
  const polygonLayerStatistics = (context.polygonLayerStatistics || []) as PolygonLayerStatistic[];
  const polygonH3Resolution = normalizeResolution((context as any).polygonH3Resolution);
  const polygonResultMap = useMemo(
    () =>
      groupPolygonAnalysisResultsByLayer(
        (context.polygonAnalysisResult || []) as PolygonAnalysisResultItem[]
      ),
    [context.polygonAnalysisResult]
  );
  const polygonLegendMap = useMemo(() => {
    return new Map(polygonLegends.map((legend) => [legend.layerId, legend] as const));
  }, [polygonLegends]);
  const polygonStatisticsMap = useMemo(() => {
    return new Map(polygonLayerStatistics.map((item) => [item.layerId, item] as const));
  }, [polygonLayerStatistics]);

  const orderedStatistics = useMemo(() => {
    if (!polygonLegends.length) {
      return polygonLayerStatistics;
    }

    return polygonLegends
      .map((legend) => polygonStatisticsMap.get(legend.layerId))
      .filter((item: PolygonLayerStatistic | undefined): item is PolygonLayerStatistic => !!item);
  }, [polygonLegends, polygonLayerStatistics, polygonStatisticsMap]);

  const clearHexagonOverlays = () => {
    if (map && hexagonOverlaysRef.current.length) {
      map.remove(hexagonOverlaysRef.current);
    }
    hexagonOverlaysRef.current = [];
  };

  const expandedLayerId = context.polygonExpandedLayerId;

  useEffect(() => {
    clearHexagonOverlays();

    if (
      context.polygonAnalysisStatus === "running" ||
      context.polygonAnalysisStatus === "error" ||
      !expandedLayerId ||
      !map ||
      !AMap
    ) {
      return;
    }

    const layerResults = polygonResultMap.get(expandedLayerId) || [];
    if (!layerResults.length) {
      return;
    }

    const legendInfo = polygonLegendMap.get(expandedLayerId);
    const shapeObj = (context.currentShape as any)?.aMapRef || context.currentShape;
      
    const overlays = buildPolygonLayerHexagons({
      AMap,
      results: layerResults,
      legendInfo,
      shape: shapeObj,
      preferredResolution: polygonH3Resolution
    });

    if (overlays.length) {
      overlays.forEach((overlay: any) => overlay.setMap(map));
      hexagonOverlaysRef.current = overlays;
    }

    return clearHexagonOverlays;
  }, [
    context.polygonAnalysisStatus,
    expandedLayerId,
    polygonResultMap,
    polygonLegendMap,
    context.currentShape,
    polygonH3Resolution,
    map,
    AMap
  ]);

  useEffect(() => {
    return () => {
      clearHexagonOverlays();
    };
  }, []);

  const toggleExpanded = (layerId: string) => {
    dispatch({
      type: AppAction.setPolygonExpandedLayerId,
      payload: expandedLayerId === layerId ? undefined : layerId
    });
  };

  const handleRetry = () => {
    dispatch({ type: AppAction.setTryAgainType, payload: "polygonAnalyse" });
  };

  if (context.polygonAnalysisStatus && context.polygonAnalysisStatus === "error") {
    return <TryAgain handleRetry={handleRetry} />;
  }

  if (context.polygonAnalysisStatus && context.polygonAnalysisStatus === "running") {
    return (
      <div className={styles.loadingWrap}>
        <LinearProgress color="success" className={styles.loadingBar} />
        <div>{t("drawShape.alalyseLoadingContent")}</div>
      </div>
    );
  }

  return (
    <Box className={styles.cardList}>
      {orderedStatistics.map((item) => {
        const legendInfo = polygonLegendMap.get(item.layerId);
        return (
          <PolygonStatisticsCard
            key={item.layerId}
            item={item}
            legendInfo={legendInfo}
            isExpanded={expandedLayerId === item.layerId}
            onToggle={toggleExpanded}
            onOpenFathom={() =>
              dispatch({ type: AppAction.setFathomIconDialog, payload: true })
            }
          />
        );
      })}
    </Box>
  );
}

export default PolygonResults;
